import React, { useState, useEffect } from 'react';
import { UIDrawerModal } from '../components/modals';
import { PopupStampcard } from '../components/popups';
import { UIIconBtn } from '../components/basic';
import { useLang } from '../../_LangContext';
import { useNavigation } from '../../_NavigationContext';
import LocaleService from '../../services/LocaleService';
import MAP_DATA from '../../../_json/_map_data.json';

const ModalStampcard = ({ isOpen, onClose, stampcardStats }) =>
{
	const { lang, getText, isLocaleReady } = useLang();
	const [selectedStamp, setSelectedStamp] = useState(null);
	const [stampData, setStampData] = useState([]);
	const { navigateToHomeDirect } = useNavigation();

	const handleHomeClick = () =>
	{
		navigateToHomeDirect();
	};

    // Load stampcard data using MAP_DATA + LocaleService
    useEffect(() => 
    {
        // Only load data when locale is ready
        if (!isLocaleReady) return;
        
        const loadStampData = () => 
        {
            try 
            {
                // Build mapping from building ID to trivia ID dynamically from MAP_DATA
                const buildingToTriviaMap = {};
                MAP_DATA.trivia?.forEach(triviaItem => {
                    buildingToTriviaMap[triviaItem.bid] = triviaItem.id;
                });

                // Get buildings that have stampcard data (those in the mapping)
                const stampcardBuildingIds = Object.keys(buildingToTriviaMap).map(id => parseInt(id));
                const stampcardBuildings = MAP_DATA.buildings.filter(building => 
                    stampcardBuildingIds.includes(building.id)
                );
                
                const stampItems = stampcardBuildings.map((building) => {
                    const triviaId = buildingToTriviaMap[building.id];
                    const stampId = triviaId;
                    
                    // Get localized building data for name
                    const localizedBuilding = LocaleService.getItemData(lang, 'buildings', building.id);
                    
                    // Get trivia data from LocaleService (description, audio)
                    const triviaData = LocaleService.getItemData(lang, 'trivia', triviaId);
                    
                    // Get trivia structural data from MAP_DATA (img, coordinates)
                    const triviaStructural = MAP_DATA.trivia?.find(item => item.id === triviaId);
                    
                    return {
                        id: stampId, // Sequential stamp ID (1-6)
                        bid: building.id, // Building ID for matching with stampcardStats
                        name: localizedBuilding?.name || building.name,
                        description: triviaData?.description || '', // Trivia content from locale
                        img: triviaStructural?.img ? `${process.env.PUBLIC_URL}/img/stampcard/${triviaStructural.img}.jpg` : null,
                        description_audio: triviaData?.description_audio || null,
                        x: building.x,
                        y: building.y
                    };
                });

                // Sort by stamp ID to maintain consistent order
                stampItems.sort((a, b) => a.id - b.id);
                setStampData(stampItems);
            } 
            catch (error) 
            {
                console.error('Failed to load stampcard data:', error);
                setStampData([]);
            }
        };

        loadStampData();
    }, 
    [lang, isLocaleReady]);

    const handleStampClick = (stamp) => {
        if (stampcardStats[stamp.bid]) {
			setSelectedStamp(stamp);
		}
	};

	const handlePopupClose = () =>
	{
		setSelectedStamp(null);
	};

	const getStampImage = (itemId) =>
	{
		const formattedId = String(itemId).padStart(2, '0');
		return `${process.env.PUBLIC_URL}/img/stampcard/stampcard_stamp_${formattedId}.png`;
	};

	return (
		<>
			<UIDrawerModal 
				className="stampcard-modal" 
				isOpen={isOpen} 
				onClose={onClose}
				shouldCloseOnOverlayClick={false}
			>
				<div className="modal-header-bar">
					<div className="left-ui">
						<div className="modal-bar-btn back" onClick={onClose}>
							<p className="icon angle-left" />
						</div>
					</div>
					<div className="right-ui sp-only">
						<UIIconBtn id="btn-map-home" className={`btn-map-home circle no-base`} btnKey="home" iconClass="home" onClick={handleHomeClick} />
					</div>
				</div>
				<div className="modal-container">
					<div className="modal-container-inner">
						<section className="stampcard">
							<div className="section-container">
								{lang === 'ja' ? (
									<h2 className={`title ${lang}`}>
										<img
											src={`${process.env.PUBLIC_URL}/img/stampcard/stampcard_title_ja.png`}
											alt={getText('stampcard')}
										/>
									</h2>
								) : (
									<h2 className={`title ${lang}`}>
										{getText('stampcard')}
									</h2>
								)}
								<div className="section-lead">
									<p>{getText('sc-desc')}</p>
								</div>
								<div className="block stamps">
									{stampData.map((item) => (
										<div
											key={item.id}
											className={`stamp-item ${stampcardStats[item.bid] ? 'collected' : ''}`}
											onClick={() => handleStampClick(item)}
										>
											<div className="item-image">
												<img
													src={getStampImage(item.id)}
													alt={item.name}
												/>
											</div>
											<div className="item-name">
												{item.name}
											</div>
										</div>
									))}
								</div>
							</div>
						</section>
					</div>
				</div>
			</UIDrawerModal>

			<PopupStampcard
				isOpen={selectedStamp !== null}
				onClose={handlePopupClose}
				data={selectedStamp}
			/>
		</>
	);
};

export default ModalStampcard;